// Leitor de código de barras USB nas telas de venda (Balcão e Novo Pedido do
// Delivery — 29/09/2026). O leitor "digita" o código muito rápido e aperta
// Enter; este hook escuta a tela inteira, separa isso da digitação de gente e
// entrega o código a `onLeitura`. O que fazer com ele é de cada tela.
import { useEffect, useRef } from "react";

// Leitor: ~5–30 ms entre teclas; gente digitando: 100+. 80 dá folga para a
// página ocupada (fotos carregando) sem confundir com digitação.
const INTERVALO_MAX_MS = 80;
const MIN_CARACTERES = 4;
const MIN_SEM_ENTER = 8; // leitor configurado sem Enter no fim

function campoAtivo() {
  const el = document.activeElement;
  return el && ["INPUT", "TEXTAREA"].includes(el.tagName) ? el : null;
}

// Muda o valor de um campo controlado pelo React: setter nativo + evento `input`.
function mudarValor(el, valor) {
  if (!el || el.value === valor) return;
  const setter = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(el), "value")?.set;
  if (!setter) return;
  setter.call(el, valor);
  el.dispatchEvent(new Event("input", { bubbles: true }));
}

export function useLeitorCodigoBarras({ ativo, onLeitura }) {
  const onLeituraRef = useRef(onLeitura);
  onLeituraRef.current = onLeitura;

  useEffect(() => {
    if (!ativo) return undefined;
    let buffer = "";
    let ultimo = 0;
    let semEnter;
    // Com o cursor num campo (nome/telefone do cliente, busca…), o leitor
    // escreveria o código lá — e campo com máscara ainda reformata. Da 2ª tecla
    // rápida em diante elas ficam SEGURADAS (não entram); só a 1ª entra, e no
    // fim o campo volta ao valor de antes. Não era o leitor (gente digitando
    // rápido)? As seguradas são devolvidas ao campo.
    let campo = null;
    let valorAntes = "";
    let seguradas = "";
    const devolverSeguradas = () => {
      if (seguradas && campo && campo === document.activeElement) mudarValor(campo, campo.value + seguradas);
      seguradas = "";
    };
    const finalizar = () => {
      const codigo = buffer;
      buffer = "";
      seguradas = "";
      mudarValor(campo, valorAntes);
      onLeituraRef.current(codigo);
    };
    const onKeyDown = (e) => {
      const agora = e.timeStamp;
      if (e.key === "Enter") {
        if (buffer.length >= MIN_CARACTERES && agora - ultimo < INTERVALO_MAX_MS * 2) {
          // Sem isto o Enter do leitor também "clica" no botão em foco.
          e.preventDefault();
          e.stopPropagation();
          clearTimeout(semEnter);
          finalizar();
          return;
        }
        devolverSeguradas();
        buffer = "";
        return;
      }
      if (e.key.length !== 1 || e.ctrlKey || e.altKey || e.metaKey) return;
      if (agora - ultimo > INTERVALO_MAX_MS) {
        devolverSeguradas();
        buffer = "";
        campo = campoAtivo();
        valorAntes = campo ? campo.value : "";
      } else if (campo) {
        e.preventDefault();
        seguradas += e.key;
      }
      buffer += e.key;
      ultimo = agora;
      clearTimeout(semEnter);
      semEnter = setTimeout(() => {
        if (buffer.length >= MIN_SEM_ENTER) finalizar();
        else { devolverSeguradas(); buffer = ""; }
      }, 150);
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => { window.removeEventListener("keydown", onKeyDown, true); clearTimeout(semEnter); };
  }, [ativo]);
}
